import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import ExitToAppRoundedIcon from '@mui/icons-material/ExitToAppRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import SearchIcon from '@mui/icons-material/Search';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';

import { collection, getDocs, query, where } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../firebase';

const auth = getAuth();

const LAB_OPTIONS = ['All Labs', 'NEXUS', 'RAISE LAB', 'SAND BOX'];

const getTimestampMs = (value) => {
  if (!value) return null;

  if (typeof value?.toMillis === 'function') {
    return value.toMillis();
  }

  if (typeof value?.seconds === 'number') {
    return value.seconds * 1000;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
};

const formatDateTime = (value) => {
  const ms = getTimestampMs(value);
  if (!ms) return '—';

  return new Date(ms).toLocaleString('en-PH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatDateOnly = (value) => {
  const ms = getTimestampMs(value);
  if (!ms) return '—';

  return new Date(ms).toLocaleDateString('en-PH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatTimeOnly = (value) => {
  const ms = getTimestampMs(value);
  if (!ms) return '—';

  return new Date(ms).toLocaleTimeString('en-PH', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatDuration = (start, end) => {
  const startMs = getTimestampMs(start);
  const endMs = getTimestampMs(end);

  if (!startMs || !endMs || endMs < startMs) return '—';

  const totalMinutes = Math.floor((endMs - startMs) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours <= 0) return `${minutes} min`;
  if (minutes <= 0) return `${hours} hr`;
  return `${hours} hr ${minutes} min`;
};

const normalizeString = (value) => (value || '').toString().trim();
const normalizeForMatch = (value) => normalizeString(value).toLowerCase();

const buildFullName = (item) => {
  const firstName = normalizeString(item.firstName);
  const lastName = normalizeString(item.lastName);
  return [firstName, lastName].filter(Boolean).join(' ').trim() || 'Unknown Student';
};

const getFieldValue = (item, keys = []) => {
  for (const key of keys) {
    const value = normalizeString(item?.[key]);
    if (value) return value;
  }

  return '';
};

const getFacultyNameCandidates = (faculty = {}) => {
  const firstName = getFieldValue(faculty, [
    'firstName',
    'First Name',
    'firstname',
    'first_name',
  ]);

  const middleName = getFieldValue(faculty, [
    'middleName',
    'Middle Name',
    'middlename',
    'middle_name',
  ]);

  const lastName = getFieldValue(faculty, [
    'lastName',
    'Last Name',
    'lastname',
    'last_name',
  ]);

  const displayName = getFieldValue(faculty, [
    'fullName',
    'Full Name',
    'name',
    'Name',
    'faculty',
    'Faculty',
    'prof',
    'Professor',
  ]);

  return [
    displayName,
    [firstName, lastName].filter(Boolean).join(' '),
    [firstName, middleName, lastName].filter(Boolean).join(' '),
    firstName && lastName ? `${lastName}, ${firstName}` : '',
    firstName && middleName && lastName ? `${lastName}, ${firstName} ${middleName}` : '',
  ]
    .map(normalizeForMatch)
    .filter(Boolean);
};

const hasAnyMatch = (left = [], right = []) => {
  return left.some((leftValue) => right.includes(leftValue));
};

const sessionBelongsToFaculty = (session, faculty, user) => {
  if (!session || !faculty) return false;

  const facultyUidCandidates = [
    user?.uid,
    faculty.authUid,
    faculty.uid,
    faculty.userId,
  ]
    .map(normalizeForMatch)
    .filter(Boolean);

  const sessionUidCandidates = [
    session.facultyAuthUid,
    session.profAuthUid,
    session.professorAuthUid,
    session.authUid,
    session.createdByUid,
    session.createdBy,
  ]
    .map(normalizeForMatch)
    .filter(Boolean);

  if (hasAnyMatch(facultyUidCandidates, sessionUidCandidates)) return true;

  const facultyIdCandidates = [
    faculty.id,
    faculty.facultyId,
    faculty.facultyNumber,
    faculty['Faculty ID'],
    faculty['Faculty Number'],
    faculty.employeeId,
  ]
    .map(normalizeForMatch)
    .filter(Boolean);

  const sessionIdCandidates = [
    session.facultyId,
    session.facultyNumber,
    session.professorId,
    session.profId,
  ]
    .map(normalizeForMatch)
    .filter(Boolean);

  if (hasAnyMatch(facultyIdCandidates, sessionIdCandidates)) return true;

  const facultyEmailCandidates = [
    user?.email,
    faculty.email,
    faculty.Email,
  ]
    .map(normalizeForMatch)
    .filter(Boolean);

  const sessionEmailCandidates = [
    session.facultyEmail,
    session.profEmail,
    session.professorEmail,
    session.email,
  ]
    .map(normalizeForMatch)
    .filter(Boolean);

  if (hasAnyMatch(facultyEmailCandidates, sessionEmailCandidates)) return true;

  const facultyNameCandidates = [
    ...getFacultyNameCandidates(faculty),
    user?.displayName,
  ]
    .map(normalizeForMatch)
    .filter(Boolean);

  const sessionNameCandidates = [
    session.prof,
    session.professor,
    session.facultyName,
    session.instructor,
    session.teacher,
  ]
    .map(normalizeForMatch)
    .filter(Boolean);

  return hasAnyMatch(facultyNameCandidates, sessionNameCandidates);
};

const getCourseShortcut = (course) => {
  const value = normalizeString(course);
  if (!value) return '—';

  const manualMap = {
    'BS Information Technology': 'BSIT',
    'BS in Information Technology': 'BSIT',
    'Bachelor of Science in Information Technology': 'BSIT',
    'BS Computer Science': 'BSCS',
    'Bachelor of Science in Computer Science': 'BSCS',
    'BS Information Systems': 'BSIS',
    'Bachelor of Science in Information Systems': 'BSIS',
  };

  if (manualMap[value]) return manualMap[value];

  const words = value.split(/\s+/).filter(Boolean);
  const upperWords = words.map((word) => word.toUpperCase());

  if (upperWords.length === 1 && /^[A-Z0-9]+$/.test(upperWords[0])) {
    return upperWords[0];
  }

  const filtered = words.filter(
    (word) =>
      !['of', 'in', 'and', 'the', 'science', 'bachelor'].includes(word.toLowerCase())
  );

  const initials = filtered.map((word) => word[0]?.toUpperCase() || '').join('');
  return initials || value;
};

const getYearShortcut = (yearLevel) => {
  const value = normalizeString(yearLevel);
  if (!value) return '';

  const manualMap = {
    '1st Year': '1',
    '2nd Year': '2',
    '3rd Year': '3',
    '4th Year': '4',
    '5th Year': '5',
    'First Year': '1',
    'Second Year': '2',
    'Third Year': '3',
    'Fourth Year': '4',
    'Fifth Year': '5',
    '1': '1',
    '2': '2',
    '3': '3',
    '4': '4',
    '5': '5',
  };

  if (manualMap[value]) return manualMap[value];

  const matchedNumber = value.match(/\d+/);
  if (matchedNumber) return matchedNumber[0];

  return value.charAt(0).toUpperCase();
};

const getClassShortcut = (course, yearLevel, section) => {
  const courseShort = getCourseShortcut(course);
  const yearShort = getYearShortcut(yearLevel);
  const secShort = normalizeString(section).replace(/\s+/g, '');

  if (!yearShort && !secShort) return courseShort;
  return `${courseShort} - ${yearShort}${secShort}`;
};

const getArrivalChipStyles = (status) => {
  if (status === 'Late') {
    return {
      backgroundColor: '#fee2e2',
      color: '#b91c1c',
    };
  }

  if (status === 'On Time') {
    return {
      backgroundColor: '#ecfdf3',
      color: '#027a48',
    };
  }

  if (status === '—') {
    return {
      backgroundColor: '#f8fafc',
      color: '#64748b',
    };
  }

  return {
    backgroundColor: '#f1f5f9',
    color: '#475569',
  };
};

const getAttendanceChipStyles = (status) => {
  if (status === 'Complete') {
    return {
      backgroundColor: '#ecfdf3',
      color: '#027a48',
    };
  }

  if (status === 'Time In Only' || status === 'Time Out Only') {
    return {
      backgroundColor: '#fff4e5',
      color: '#b54708',
    };
  }

  if (status === '—') {
    return {
      backgroundColor: '#f8fafc',
      color: '#64748b',
    };
  }

  return {
    backgroundColor: '#f1f5f9',
    color: '#475569',
  };
};

const csvEscape = (value) => {
  const stringValue = value == null ? '' : String(value);
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const Reports = () => {
  const isMobile = useMediaQuery('(max-width:900px)');

  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentFaculty, setCurrentFaculty] = useState(null);
  const [userRole, setUserRole] = useState('Admin');

  const [sessions, setSessions] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [studentsMaster, setStudentsMaster] = useState([]);
  const [alertState, setAlertState] = useState({
    error: '',
    success: '',
    warning: '',
  });

  const [labFilter, setLabFilter] = useState('All Labs');
  const [profFilter, setProfFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [page, setPage] = useState(0);
  const rowsPerPage = 5;

  const [selectedSession, setSelectedSession] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const isFacultyView = normalizeForMatch(userRole) === 'faculty';

  useEffect(() => {
    if (alertState.error || alertState.success || alertState.warning) {
      const timer = setTimeout(() => {
        setAlertState({ error: '', success: '', warning: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [alertState]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setAuthReady(false);
      setCurrentUser(user);
      setCurrentFaculty(null);

      const storedRole = normalizeString(
        localStorage.getItem('userRole') || localStorage.getItem('role')
      );

      if (!user) {
        setUserRole(storedRole || 'Admin');
        setAuthReady(true);
        return;
      }

      try {
        const userUid = normalizeString(user.uid);
        const userEmail = normalizeString(user.email);
        let matchedFaculty = null;

        if (userUid) {
          const facultyByUidSnap = await getDocs(
            query(collection(db, 'Faculties'), where('authUid', '==', userUid))
          );

          if (!facultyByUidSnap.empty) {
            const facultyDoc = facultyByUidSnap.docs[0];
            matchedFaculty = {
              id: facultyDoc.id,
              ...facultyDoc.data(),
            };
          }
        }

        if (!matchedFaculty && userEmail) {
          const allFacultySnap = await getDocs(collection(db, 'Faculties'));

          const matchedDoc = allFacultySnap.docs.find((facultyDoc) => {
            const faculty = facultyDoc.data();

            const uidCandidates = [
              faculty.authUid,
              faculty.uid,
              faculty.userId,
            ]
              .map(normalizeForMatch)
              .filter(Boolean);

            const emailCandidates = [
              faculty.email,
              faculty.Email,
            ]
              .map(normalizeForMatch)
              .filter(Boolean);

            return (
              uidCandidates.includes(normalizeForMatch(userUid)) ||
              emailCandidates.includes(normalizeForMatch(userEmail))
            );
          });

          if (matchedDoc) {
            matchedFaculty = {
              id: matchedDoc.id,
              ...matchedDoc.data(),
            };
          }
        }

        if (matchedFaculty) {
          setCurrentFaculty(matchedFaculty);
          setUserRole(
            normalizeString(matchedFaculty.userRole || matchedFaculty.role || storedRole || 'Faculty')
          );
        } else {
          setCurrentFaculty({
            authUid: user.uid,
            email: user.email,
            fullName: user.displayName,
          });
          setUserRole(storedRole || 'Admin');
        }
      } catch (error) {
        console.error('Error loading current faculty profile:', error);
        setCurrentFaculty({
          authUid: user.uid,
          email: user.email,
          fullName: user.displayName,
        });
        setUserRole(storedRole || 'Admin');
      } finally {
        setAuthReady(true);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchReportsData = async () => {
    try {
      setLoading(true);

      const [sessionsSnap, studentsSnap] = await Promise.all([
        getDocs(collection(db, 'Sessions')),
        getDocs(collection(db, 'Students')),
      ]);

      const allSessionsData = sessionsSnap.docs.map((sessionDoc) => ({
        id: sessionDoc.id,
        ...sessionDoc.data(),
      }));

      const sessionsData = isFacultyView
        ? allSessionsData.filter((session) =>
            sessionBelongsToFaculty(session, currentFaculty, currentUser)
          )
        : allSessionsData;

      const studentsData = studentsSnap.docs.map((studentDoc) => ({
        id: studentDoc.id,
        ...studentDoc.data(),
      }));

      const attendanceResults = await Promise.all(
        sessionsData.map(async (session) => {
          const attendanceSnap = await getDocs(
            collection(db, 'Sessions', session.id, 'Attendance')
          );

          return attendanceSnap.docs.map((attendanceDoc) => ({
            id: attendanceDoc.id,
            sessionId: session.id,
            ...attendanceDoc.data(),
          }));
        })
      );

      const attendanceData = attendanceResults.flat();

      setSessions(sessionsData);
      setAttendance(attendanceData);
      setStudentsMaster(studentsData);
      setPage(0);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setAlertState({
        error: 'Failed to load reports data.',
        success: '',
        warning: '',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authReady) {
      fetchReportsData();
    }
  }, [authReady, userRole, currentFaculty?.id, currentUser?.uid]);

  const attendanceBySession = useMemo(() => {
    const grouped = {};

    attendance.forEach((item) => {
      const sessionId = normalizeString(item.sessionId);
      if (!sessionId) return;

      if (!grouped[sessionId]) grouped[sessionId] = [];
      grouped[sessionId].push(item);
    });

    return grouped;
  }, [attendance]);

  const processedSessions = useMemo(() => {
    return sessions
      .map((session) => {
        const sessionAttendance = attendanceBySession[session.id] || [];

        const matchedStudents = studentsMaster.filter((student) => {
          return (
            normalizeForMatch(student.course) === normalizeForMatch(session.course) &&
            normalizeForMatch(student.yearLevel) === normalizeForMatch(session.yearLevel) &&
            normalizeForMatch(student.section) === normalizeForMatch(session.section)
          );
        });

        const attendanceLookup = new Map();

        sessionAttendance.forEach((record) => {
          const studentNumberKey = normalizeForMatch(record.studentNumber || record.studentId);
          const rfidKey = normalizeForMatch(record.rfid);
          const fullNameKey = normalizeForMatch(buildFullName(record));

          const prepared = {
            id: record.id,
            studentId: normalizeString(record.studentId || record.studentNumber),
            studentNumber: normalizeString(record.studentNumber || record.studentId),
            rfid: normalizeString(record.rfid),
            firstName: normalizeString(record.firstName),
            lastName: normalizeString(record.lastName),
            fullName: buildFullName(record),
            timeIn: record.timeIn || null,
            timeOut: record.timeOut || null,
            timeInMs: getTimestampMs(record.timeIn),
            timeOutMs: getTimestampMs(record.timeOut),
            status: normalizeString(record.status) || '—',
          };

          if (studentNumberKey) attendanceLookup.set(`studentNumber:${studentNumberKey}`, prepared);
          if (rfidKey) attendanceLookup.set(`rfid:${rfidKey}`, prepared);
          if (fullNameKey) attendanceLookup.set(`name:${fullNameKey}`, prepared);
        });

        const mergedStudents = matchedStudents.map((student) => {
          const studentNumberKey = normalizeForMatch(student.studentNumber);
          const rfidKey = normalizeForMatch(student.rfid);
          const fullName = buildFullName(student);
          const fullNameKey = normalizeForMatch(fullName);

          const matchedRecord =
            attendanceLookup.get(`studentNumber:${studentNumberKey}`) ||
            attendanceLookup.get(`rfid:${rfidKey}`) ||
            attendanceLookup.get(`name:${fullNameKey}`);

          if (matchedRecord) {
            const attendanceStatus =
              matchedRecord.timeInMs && matchedRecord.timeOutMs
                ? 'Complete'
                : matchedRecord.timeInMs
                ? 'Time In Only'
                : matchedRecord.timeOutMs
                ? 'Time Out Only'
                : '—';

            return {
              id: student.id,
              studentNumber: normalizeString(student.studentNumber),
              firstName: normalizeString(student.firstName),
              lastName: normalizeString(student.lastName),
              fullName,
              timeIn: matchedRecord.timeIn,
              timeOut: matchedRecord.timeOut,
              timeInMs: matchedRecord.timeInMs,
              timeOutMs: matchedRecord.timeOutMs,
              status: matchedRecord.status || '—',
              attendanceStatus,
              hasTapped: !!(matchedRecord.timeInMs || matchedRecord.timeOutMs),
            };
          }

          return {
            id: student.id,
            studentNumber: normalizeString(student.studentNumber),
            firstName: normalizeString(student.firstName),
            lastName: normalizeString(student.lastName),
            fullName,
            timeIn: null,
            timeOut: null,
            timeInMs: null,
            timeOutMs: null,
            status: '—',
            attendanceStatus: '—',
            hasTapped: false,
          };
        });

        const unmatchedAttendanceStudents = sessionAttendance
          .filter((record) => {
            const recordStudentNumber = normalizeForMatch(record.studentNumber || record.studentId);
            const recordRfid = normalizeForMatch(record.rfid);
            const recordName = normalizeForMatch(buildFullName(record));

            return !matchedStudents.some((student) => {
              return (
                normalizeForMatch(student.studentNumber) === recordStudentNumber ||
                normalizeForMatch(student.rfid) === recordRfid ||
                normalizeForMatch(buildFullName(student)) === recordName
              );
            });
          })
          .map((record) => {
            const timeInMs = getTimestampMs(record.timeIn);
            const timeOutMs = getTimestampMs(record.timeOut);

            return {
              id: record.id,
              studentNumber: normalizeString(record.studentNumber || record.studentId),
              firstName: normalizeString(record.firstName),
              lastName: normalizeString(record.lastName),
              fullName: buildFullName(record),
              timeIn: record.timeIn || null,
              timeOut: record.timeOut || null,
              timeInMs,
              timeOutMs,
              status: normalizeString(record.status) || '—',
              attendanceStatus:
                timeInMs && timeOutMs
                  ? 'Complete'
                  : timeInMs
                  ? 'Time In Only'
                  : timeOutMs
                  ? 'Time Out Only'
                  : '—',
              hasTapped: !!(timeInMs || timeOutMs),
            };
          });

        const students = [...mergedStudents, ...unmatchedAttendanceStudents].sort((a, b) => {
          if (a.hasTapped !== b.hasTapped) {
            return a.hasTapped ? -1 : 1;
          }

          const aLatest = Math.max(a.timeInMs || 0, a.timeOutMs || 0);
          const bLatest = Math.max(b.timeInMs || 0, b.timeOutMs || 0);

          if (a.hasTapped && b.hasTapped && aLatest !== bLatest) {
            return bLatest - aLatest;
          }

          return a.fullName.localeCompare(b.fullName);
        });

        const completeCount = students.filter((s) => s.attendanceStatus === 'Complete').length;
        const incompleteCount = students.filter(
          (s) => s.attendanceStatus === 'Time In Only' || s.attendanceStatus === 'Time Out Only'
        ).length;
        const onTimeCount = students.filter((s) => s.status === 'On Time').length;
        const lateCount = students.filter((s) => s.status === 'Late').length;

        return {
          ...session,
          startMs: getTimestampMs(session.startTime),
          endMs: getTimestampMs(session.endTime),
          students,
          totalStudents: students.length,
          completeCount,
          incompleteCount,
          onTimeCount,
          lateCount,
          durationLabel: formatDuration(session.startTime, session.endTime),
          classShortcut: getClassShortcut(session.course, session.yearLevel, session.section),
        };
      })
      .sort((a, b) => (b.startMs || 0) - (a.startMs || 0));
  }, [sessions, attendanceBySession, studentsMaster]);

  const filteredSessions = useMemo(() => {
    return processedSessions.filter((session) => {
      const lab = normalizeString(session.selectedLab);
      const prof = normalizeString(session.prof).toLowerCase();
      const course = normalizeString(session.course).toLowerCase();
      const subject = normalizeString(session.subject).toLowerCase();
      const section = normalizeString(session.section).toLowerCase();
      const yearLevel = normalizeString(session.yearLevel).toLowerCase();

      const fromMs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
      const toMs = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : null;
      const sessionMs = session.startMs || session.endMs || null;
      const keyword = searchTerm.trim().toLowerCase();

      if (labFilter !== 'All Labs' && lab !== labFilter) return false;
      if (profFilter && !prof.includes(profFilter.toLowerCase())) return false;
      if (courseFilter && !course.includes(courseFilter.toLowerCase())) return false;
      if (subjectFilter && !subject.includes(subjectFilter.toLowerCase())) return false;
      if (sectionFilter && !section.includes(sectionFilter.toLowerCase())) return false;
      if (yearFilter && !yearLevel.includes(yearFilter.toLowerCase())) return false;
      if (fromMs && sessionMs && sessionMs < fromMs) return false;
      if (toMs && sessionMs && sessionMs > toMs) return false;

      if (keyword) {
        const searchableText = [
          session.prof,
          session.subject,
          session.course,
          session.section,
          session.yearLevel,
          session.selectedLab,
          formatDateOnly(session.startTime || session.endTime),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!searchableText.includes(keyword)) return false;
      }

      return true;
    });
  }, [
    processedSessions,
    labFilter,
    profFilter,
    courseFilter,
    subjectFilter,
    sectionFilter,
    yearFilter,
    dateFrom,
    dateTo,
    searchTerm,
  ]);

  const paginatedSessions = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredSessions.slice(start, start + rowsPerPage);
  }, [filteredSessions, page]);

  const activeFilterCount = useMemo(() => {
    return [
      labFilter !== 'All Labs',
      !!profFilter,
      !!courseFilter,
      !!subjectFilter,
      !!sectionFilter,
      !!yearFilter,
      !!dateFrom,
      !!dateTo,
    ].filter(Boolean).length;
  }, [labFilter, profFilter, courseFilter, subjectFilter, sectionFilter, yearFilter, dateFrom, dateTo]);

  const clearFilters = () => {
    setLabFilter('All Labs');
    setProfFilter('');
    setCourseFilter('');
    setSubjectFilter('');
    setSectionFilter('');
    setYearFilter('');
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
    setPage(0);
  };

  const handleOpenDetails = (session) => {
    setSelectedSession(session);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setSelectedSession(null);
    setDetailsOpen(false);
  };

  const exportCsv = (headers, rows, filename) => {
    const csvContent = [headers, ...rows]
      .map((row) => row.map(csvEscape).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportReports = () => {
    if (!filteredSessions.length) {
      setAlertState({
        error: '',
        success: '',
        warning: 'No reports available to export.',
      });
      return;
    }

    const headers = [
      'Date',
      'Laboratory',
      'Professor',
      'Subject',
      'Course',
      'Class',
      'Start Time',
      'End Time',
      'Duration',
      'Total Students',
      'Complete',
      'Incomplete',
      'On Time',
      'Late',
    ];

    const rows = filteredSessions.map((session) => [
      formatDateOnly(session.startTime || session.endTime),
      session.selectedLab || '—',
      session.prof || '—',
      session.subject || '—',
      session.course || '—',
      session.classShortcut || '—',
      formatTimeOnly(session.startTime),
      session.endTime ? formatTimeOnly(session.endTime) : 'Ongoing',
      session.durationLabel || '—',
      session.totalStudents || 0,
      session.completeCount || 0,
      session.incompleteCount || 0,
      session.onTimeCount || 0,
      session.lateCount || 0,
    ]);

    const today = new Date().toISOString().slice(0, 10);
    exportCsv(headers, rows, `session-reports-${today}.csv`);

    setAlertState({
      error: '',
      success: 'Reports exported successfully.',
      warning: '',
    });
  };

  const handleExportSelectedSession = () => {
    if (!selectedSession) {
      setAlertState({
        error: '',
        success: '',
        warning: 'No session selected to export.',
      });
      return;
    }

    const headers = ['Student', 'Time In', 'Time Out', 'Attendance', 'Arrival'];

    const rows = selectedSession.students.map((student) => [
      student.fullName || '—',
      student.timeIn ? formatDateTime(student.timeIn) : '—',
      student.timeOut ? formatDateTime(student.timeOut) : '—',
      student.attendanceStatus || '—',
      student.status || '—',
    ]);

    const safeDate = formatDateOnly(selectedSession.startTime || selectedSession.endTime).replace(
      /[^\w-]/g,
      '_'
    );
    const safeLab = normalizeString(selectedSession.selectedLab || 'session').replace(/[^\w-]/g, '_');

    exportCsv(headers, rows, `session-details-${safeLab}-${safeDate}.csv`);

    setAlertState({
      error: '',
      success: 'Session details exported successfully.',
      warning: '',
    });
  };

  const searchFieldStyles = {
    width: { xs: '100%', sm: '320px' },
    '& .MuiOutlinedInput-root': {
      height: '42px',
      borderRadius: '12px',
      fontFamily: 'Poppins, sans-serif',
      backgroundColor: '#ffffff',
      transition: 'all 0.3s ease',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(0, 0, 0, 0.23)',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#130f72',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#130f72',
      },
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Poppins, sans-serif',
      fontSize: '0.9rem',
      '&::placeholder': {
        color: 'grey.400',
        opacity: 1,
      },
    },
  };

  const iconButtonStyles = {
    width: 42,
    height: 42,
    minWidth: 42,
    borderRadius: '12px',
    backgroundColor: '#130f72',
    color: '#ffc400',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#0f0c5c',
      boxShadow: '0 2px 8px rgba(19, 15, 114, 0.2)',
    },
    '&:active': {
      backgroundColor: '#0b0947',
    },
  };

  const mainButtonStyles = {
    py: 1,
    borderRadius: '12px',
    boxShadow: 'none',
    backgroundColor: '#130f72 !important',
    color: '#ffc400',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 700,
    textTransform: 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#0f0c5c !important',
      boxShadow: '0 2px 8px rgba(19, 15, 114, 0.2)',
    },
    '&:active': {
      backgroundColor: '#0b0947 !important',
    },
  };

  const tooltipSlotProps = {
    tooltip: {
      sx: {
        bgcolor: '#130f72',
        color: '#ffc400',
        fontFamily: 'Poppins, sans-serif',
        fontSize: '0.82rem',
        fontWeight: 600,
        borderRadius: '10px',
        boxShadow: '0 6px 18px rgba(19, 15, 114, 0.25)',
        px: 1.25,
        py: 0.8,
      },
    },
    arrow: {
      sx: {
        color: '#130f72',
      },
    },
  };

  const filterInputStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      fontFamily: 'Poppins, sans-serif',
      backgroundColor: '#ffffff',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#130f72',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#130f72',
      },
    },
    '& .MuiInputLabel-root': {
      fontFamily: 'Poppins, sans-serif',
      color: '#130f72',
      '&.Mui-focused': {
        color: '#130f72',
      },
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Poppins, sans-serif',
      fontSize: '0.9rem',
    },
    '& .MuiSelect-select': {
      fontFamily: 'Poppins, sans-serif',
      fontSize: '0.9rem',
    },
  };

  const topAlertStyles = {
    borderRadius: '12px',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 700,
    boxShadow: '0 10px 24px rgba(0,0,0,0.25)',
    color: '#fff',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '& .MuiAlert-message': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '& .MuiAlert-icon': {
      color: '#fff',
    },
  };

  const renderEmptyStateRow = (message, colSpan) => (
    <TableRow>
      <TableCell colSpan={colSpan} align="center" sx={{ py: 7, borderBottom: 'none' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              backgroundColor: 'rgba(19, 15, 114, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <InboxRoundedIcon
              sx={{
                fontSize: '2.2rem',
                color: '#130f72',
              }}
            />
          </Box>

          <Typography
            sx={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontWeight: 700,
              color: '#130f72',
              fontSize: '1.05rem',
            }}
          >
            No records found
          </Typography>

          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              color: 'grey.400',
              fontSize: '0.92rem',
            }}
          >
            {message}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  if (loading || !authReady) {
    return (
      <Box
        sx={{
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#130f72', mb: 2 }} />
          <Typography sx={{ color: '#130f72', fontWeight: 700 }}>
            Loading reports...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          position: 'fixed',
          top: 45,
          right: 55,
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: 'auto',
          maxWidth: '420px',
        }}
      >
        {!!alertState.error && (
          <Alert
            severity="error"
            variant="filled"
            sx={{
              ...topAlertStyles,
              bgcolor: '#b90d0d',
            }}
          >
            {alertState.error}
          </Alert>
        )}

        {!!alertState.warning && (
          <Alert
            severity="warning"
            variant="filled"
            sx={{
              ...topAlertStyles,
              bgcolor: '#b7791f',
            }}
          >
            {alertState.warning}
          </Alert>
        )}

        {!!alertState.success && (
          <Alert
            severity="success"
            variant="filled"
            sx={{
              ...topAlertStyles,
              bgcolor: '#0f56b3',
            }}
          >
            {alertState.success}
          </Alert>
        )}
      </Box>

      <Box>
        <Paper
          sx={{
            p: 3,
            m: 5,
            mt: '33px',
            pl: '40px',
            borderRadius: '15px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', md: 'center' },
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              mb: 2.5,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Space Grotesk, sans-serif',
                  color: '#130f72',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                }}
              >
                {isFacultyView ? 'My Session Reports' : 'Session Reports'}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  color: 'grey.400',
                  fontSize: '0.92rem',
                  mt: 0.35,
                }}
              >
                {isFacultyView
                  ? 'View only the lab session reports assigned to your faculty account.'
                  : 'View each lab session with professor, subject, and student attendance summary.'}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
              }}
            >
              <TextField
                placeholder="Search reports"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                sx={searchFieldStyles}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon
                        sx={{
                          color: 'grey.400',
                          '.Mui-focused &': {
                            color: '#130f72',
                          },
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />

              <Tooltip title="Export Report" arrow slotProps={tooltipSlotProps}>
                <span>
                  <Button
                    variant="contained"
                    color="inherit"
                    onClick={handleExportReports}
                    aria-label="Export Report"
                    sx={iconButtonStyles}
                  >
                    <DownloadRoundedIcon sx={{ fontSize: '1.65rem' }} />
                  </Button>
                </span>
              </Tooltip>

              <Button
                variant="contained"
                color="inherit"
                startIcon={<FilterAltRoundedIcon sx={{ color: '#ffc400' }} />}
                onClick={() => setFiltersOpen(true)}
                sx={mainButtonStyles}
              >
                Filters{activeFilterCount ? ` (${activeFilterCount})` : ''}
              </Button>
            </Box>
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              borderRadius: '15px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
              overflow: 'hidden',
            }}
          >
            <Table
              aria-label="Reports table"
              sx={{
                '& .MuiTableCell-root': {
                  py: 2,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 700,
                      color: '#111827',
                      whiteSpace: 'nowrap',
                      fontSize: '0.95rem',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarMonthRoundedIcon sx={{ p: 1, fontSize: '2rem' }} />
                      Date
                    </Box>
                  </TableCell>

                  <TableCell
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 700,
                      color: '#111827',
                      whiteSpace: 'nowrap',
                      fontSize: '0.95rem',
                    }}
                  >
                    Laboratory
                  </TableCell>

                  <TableCell
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 700,
                      color: '#111827',
                      whiteSpace: 'nowrap',
                      fontSize: '0.95rem',
                    }}
                  >
                    Professor
                  </TableCell>

                  <TableCell
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 700,
                      color: '#111827',
                      whiteSpace: 'nowrap',
                      fontSize: '0.95rem',
                    }}
                  >
                    Subject
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 700,
                      color: '#111827',
                      whiteSpace: 'nowrap',
                      fontSize: '0.95rem',
                    }}
                  >
                    Class
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 700,
                      color: '#111827',
                      whiteSpace: 'nowrap',
                      fontSize: '0.95rem',
                    }}
                  >
                    Time
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 700,
                      color: '#111827',
                      whiteSpace: 'nowrap',
                      fontSize: '0.95rem',
                    }}
                  >
                    Students
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 700,
                      color: '#111827',
                      whiteSpace: 'nowrap',
                      fontSize: '0.95rem',
                    }}
                  >
                    Arrival
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  />
                </TableRow>
              </TableHead>

              <TableBody>
                {!paginatedSessions.length ? (
                  renderEmptyStateRow(
                    searchTerm.trim() || activeFilterCount
                      ? 'No reports matched your search or filters.'
                      : isFacultyView
                      ? 'No session reports are assigned to your faculty account yet.'
                      : 'There are no session reports in the database yet.',
                    9
                  )
                ) : (
                  paginatedSessions.map((session) => (
                    <TableRow key={session.id}>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CalendarMonthRoundedIcon
                            sx={{
                              backgroundColor: '#130f72',
                              color: '#ffc400',
                              borderRadius: '50%',
                              mr: 2,
                              p: 1,
                              fontSize: '2rem',
                            }}
                          />
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: 'Poppins, sans-serif',
                                fontWeight: 700,
                                color: '#111827',
                                fontSize: '0.92rem',
                              }}
                            >
                              {formatDateOnly(session.startTime || session.endTime)}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: 'Poppins, sans-serif',
                                color: 'grey.400',
                                fontSize: '0.82rem',
                              }}
                            >
                              {session.durationLabel}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        <Chip
                          label={session.selectedLab || '—'}
                          sx={{
                            backgroundColor: '#f4f6ff',
                            color: '#130f72',
                            fontWeight: 700,
                            fontFamily: 'Poppins, sans-serif',
                          }}
                        />
                      </TableCell>

                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        <Typography
                          sx={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 700,
                            color: '#111827',
                            fontSize: '0.92rem',
                          }}
                        >
                          {session.prof || '—'}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        <Box>
                          <Typography
                            sx={{
                              fontFamily: 'Poppins, sans-serif',
                              fontWeight: 700,
                              color: '#111827',
                              fontSize: '0.92rem',
                            }}
                          >
                            {session.subject || '—'}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: 'Poppins, sans-serif',
                              color: 'grey.400',
                              fontSize: '0.82rem',
                            }}
                          >
                            {session.course || '—'}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        <Typography
                          sx={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 700,
                            color: '#111827',
                            fontSize: '0.92rem',
                          }}
                        >
                          {session.classShortcut || '—'}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        <Box>
                          <Typography
                            sx={{
                              fontFamily: 'Poppins, sans-serif',
                              fontWeight: 700,
                              color: '#111827',
                              fontSize: '0.92rem',
                            }}
                          >
                            {formatTimeOnly(session.startTime)}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: 'Poppins, sans-serif',
                              color: 'grey.400',
                              fontSize: '0.82rem',
                            }}
                          >
                            {session.endTime ? `to ${formatTimeOnly(session.endTime)}` : 'Ongoing'}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Poppins, sans-serif',
                              fontWeight: 800,
                              color: '#111827',
                              fontSize: '1rem',
                            }}
                          >
                            {session.totalStudents}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: 'Poppins, sans-serif',
                              color: 'grey.400',
                              fontSize: '0.82rem',
                            }}
                          >
                            {session.completeCount} complete
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        <Stack
                          direction="row"
                          spacing={0.8}
                          flexWrap="wrap"
                          useFlexGap
                          justifyContent="center"
                        >
                          {!!session.onTimeCount && (
                            <Chip
                              size="small"
                              label={`${session.onTimeCount} on time`}
                              sx={{
                                backgroundColor: '#ecfdf3',
                                color: '#027a48',
                                fontWeight: 700,
                                fontFamily: 'Poppins, sans-serif',
                              }}
                            />
                          )}

                          {!!session.lateCount && (
                            <Chip
                              size="small"
                              label={`${session.lateCount} late`}
                              sx={{
                                backgroundColor: '#fee2e2',
                                color: '#b91c1c',
                                fontWeight: 700,
                                fontFamily: 'Poppins, sans-serif',
                              }}
                            />
                          )}

                          {!!session.incompleteCount && (
                            <Chip
                              size="small"
                              label={`${session.incompleteCount} incomplete`}
                              sx={{
                                backgroundColor: '#fff4e5',
                                color: '#b54708',
                                fontWeight: 700,
                                fontFamily: 'Poppins, sans-serif',
                              }}
                            />
                          )}

                          {!session.onTimeCount &&
                            !session.lateCount &&
                            !session.incompleteCount && (
                              <Typography
                                sx={{
                                  fontFamily: 'Poppins, sans-serif',
                                  color: 'grey.400',
                                  fontSize: '0.85rem',
                                }}
                              >
                                —
                              </Typography>
                            )}
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        <Button
                          variant="contained"
                          color="inherit"
                          startIcon={<VisibilityRoundedIcon sx={{ color: '#ffc400' }} />}
                          onClick={() => handleOpenDetails(session)}
                          sx={{
                            ...mainButtonStyles,
                            py: 0.9,
                            px: 1.8,
                            minWidth: 115,
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={filteredSessions.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPageOptions={[]}
              labelRowsPerPage=""
              sx={{
                fontFamily: 'Poppins, sans-serif',
                '& .MuiTablePagination-toolbar': {
                  px: 2,
                  minHeight: 60,
                },
                '& .MuiTablePagination-selectLabel': {
                  display: 'none',
                },
                '& .MuiTablePagination-input': {
                  display: 'none',
                },
                '& .MuiTablePagination-select': {
                  display: 'none',
                },
                '& .MuiTablePagination-selectIcon': {
                  display: 'none',
                },
                '& .MuiTablePagination-displayedRows': {
                  fontFamily: 'Poppins, sans-serif',
                  color: 'grey.400',
                },
                '& .MuiTablePagination-actions': {
                  '& .MuiIconButton-root': {
                    color: '#130f72',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      color: '#0f0c5c',
                    },
                    '&.Mui-disabled': {
                      color: 'grey.400',
                    },
                  },
                },
                '& .MuiSvgIcon-root': {
                  color: 'inherit',
                },
              }}
            />
          </TableContainer>
        </Paper>
      </Box>

      <Dialog
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: '24px',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: '#ffffff',
          }}
        >
          <Stack
            direction={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'flex-start' : 'center'}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 800,
                  fontSize: '1.25rem',
                  color: '#130f72',
                }}
              >
                Filter Reports
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  color: '#64748b',
                  fontSize: '0.92rem',
                  mt: 0.5,
                }}
              >
                Apply filters to narrow down session reports.
              </Typography>
            </Box>

            <Chip
              label={`${activeFilterCount} active filter${activeFilterCount !== 1 ? 's' : ''}`}
              sx={{
                backgroundColor: '#f4f6ff',
                color: '#130f72',
                fontWeight: 800,
                fontFamily: 'Poppins, sans-serif',
              }}
            />
          </Stack>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ px: 3, py: 3, backgroundColor: '#f8fbff' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                fullWidth
                label="Laboratory"
                value={labFilter}
                onChange={(e) => {
                  setLabFilter(e.target.value);
                  setPage(0);
                }}
                sx={filterInputStyles}
              >
                {LAB_OPTIONS.map((lab) => (
                  <MenuItem key={lab} value={lab}>
                    {lab}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Professor"
                value={profFilter}
                onChange={(e) => {
                  setProfFilter(e.target.value);
                  setPage(0);
                }}
                sx={filterInputStyles}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Course"
                value={courseFilter}
                onChange={(e) => {
                  setCourseFilter(e.target.value);
                  setPage(0);
                }}
                sx={filterInputStyles}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Subject"
                value={subjectFilter}
                onChange={(e) => {
                  setSubjectFilter(e.target.value);
                  setPage(0);
                }}
                sx={filterInputStyles}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Section"
                value={sectionFilter}
                onChange={(e) => {
                  setSectionFilter(e.target.value);
                  setPage(0);
                }}
                sx={filterInputStyles}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Year Level"
                value={yearFilter}
                onChange={(e) => {
                  setYearFilter(e.target.value);
                  setPage(0);
                }}
                sx={filterInputStyles}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date From"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                sx={filterInputStyles}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date To"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                sx={filterInputStyles}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2.2, backgroundColor: '#ffffff', gap: 1, flexWrap: 'wrap' }}>
          <Button
            onClick={clearFilters}
            variant="outlined"
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              fontFamily: 'Poppins, sans-serif',
              borderColor: '#dbe4f0',
              color: '#130f72',
              '&:hover': {
                borderColor: '#130f72',
                backgroundColor: '#f8f9ff',
              },
            }}
          >
            Clear Filters
          </Button>

          <Button
            onClick={() => setFiltersOpen(false)}
            variant="contained"
            color="inherit"
            startIcon={<FilterAltRoundedIcon sx={{ color: '#ffc400' }} />}
            sx={mainButtonStyles}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: '24px',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ px: 3, py: 2, backgroundColor: '#ffffff' }} />

        <Divider />

        <DialogContent sx={{ px: 3, py: 3, backgroundColor: '#f8fbff' }}>
          {selectedSession && (
            <Stack spacing={2.2}>
              <>
                <Grid container spacing={2}>
                  {[
                    {
                      icon: <PersonRoundedIcon sx={{ color: '#130f72' }} />,
                      label: 'Professor',
                      value: selectedSession.prof || '—',
                    },
                    {
                      icon: <SchoolRoundedIcon sx={{ color: '#130f72' }} />,
                      label: 'Class',
                      value: selectedSession.classShortcut || '—',
                    },
                    {
                      icon: <MenuBookRoundedIcon sx={{ color: '#130f72' }} />,
                      label: 'Subject',
                      value: selectedSession.subject || '—',
                    },
                    {
                      icon: <ApartmentRoundedIcon sx={{ color: '#130f72' }} />,
                      label: 'Laboratory',
                      value: selectedSession.selectedLab || '—',
                    },
                  ].map((item) => (
                    <Grid item xs={12} sm={6} md={3} key={item.label}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          borderRadius: '18px',
                          backgroundColor: '#ffffff',
                          border: '1px solid #e8eef6',
                          height: '100%',
                        }}
                      >
                        <Stack direction="row" spacing={1.8} alignItems="center">
                          <Box
                            sx={{
                              width: 42,
                              height: 42,
                              borderRadius: '12px',
                              backgroundColor: '#f4f6ff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            {item.icon}
                          </Box>

                          <Box>
                            <Typography
                              sx={{
                                fontFamily: 'Poppins, sans-serif',
                                fontSize: '0.82rem',
                                color: '#64748b',
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: 'Poppins, sans-serif',
                                fontWeight: 700,
                                color: '#111827',
                                fontSize: '0.96rem',
                                wordBreak: 'break-word',
                              }}
                            >
                              {item.value}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                <Grid container spacing={2}>
                  {[
                    {
                      icon: <CalendarMonthRoundedIcon sx={{ color: '#130f72' }} />,
                      label: 'Date',
                      value: formatDateOnly(selectedSession.startTime || selectedSession.endTime),
                    },
                    {
                      icon: <AccessTimeRoundedIcon sx={{ color: '#130f72' }} />,
                      label: 'Schedule',
                      value: `${formatTimeOnly(selectedSession.startTime)} ${
                        selectedSession.endTime ? `to ${formatTimeOnly(selectedSession.endTime)}` : ''
                      } • ${selectedSession.durationLabel}`,
                    },
                    {
                      icon: <GroupsRoundedIcon sx={{ color: '#130f72' }} />,
                      label: 'Total Students',
                      value: `${selectedSession.totalStudents || 0}`,
                    },
                  ].map((item) => (
                    <Grid item xs={12} md={4} key={item.label}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          borderRadius: '18px',
                          backgroundColor: '#ffffff',
                          border: '1px solid #e8eef6',
                          height: '100%',
                        }}
                      >
                        <Stack direction="row" spacing={1.8} alignItems="center">
                          <Box
                            sx={{
                              width: 42,
                              height: 42,
                              borderRadius: '12px',
                              backgroundColor: '#f4f6ff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            {item.icon}
                          </Box>

                          <Box>
                            <Typography
                              sx={{
                                fontFamily: 'Poppins, sans-serif',
                                fontSize: '0.82rem',
                                color: '#64748b',
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: 'Poppins, sans-serif',
                                fontWeight: 700,
                                color: '#111827',
                                fontSize: '0.96rem',
                                wordBreak: 'break-word',
                              }}
                            >
                              {item.value}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: '20px',
                  overflow: 'hidden',
                  backgroundColor: '#ffffff',
                  border: '1px solid #e8eef6',
                }}
              >
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        {['Student', 'Time In', 'Time Out', 'Attendance', 'Arrival'].map((header) => (
                          <TableCell
                            key={header}
                            sx={{
                              fontFamily: 'Poppins, sans-serif',
                              fontWeight: 800,
                              color: '#130f72',
                              backgroundColor: '#f8fbff',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {!selectedSession.students.length ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography
                              sx={{
                                fontFamily: 'Poppins, sans-serif',
                                color: '#64748b',
                                fontWeight: 700,
                              }}
                            >
                              No attendance records found for this session.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        selectedSession.students.map((student, index) => (
                          <TableRow key={`${student.studentNumber || student.fullName}-${index}`}>
                            <TableCell>
                              <Typography
                                sx={{
                                  fontFamily: 'Poppins, sans-serif',
                                  fontWeight: 700,
                                  color: '#111827',
                                  fontSize: '0.92rem',
                                }}
                              >
                                {student.fullName}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>
                              {student.timeIn ? formatDateTime(student.timeIn) : '—'}
                            </TableCell>

                            <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>
                              {student.timeOut ? formatDateTime(student.timeOut) : '—'}
                            </TableCell>

                            <TableCell>
                              <Chip
                                size="small"
                                label={student.attendanceStatus || '—'}
                                sx={{
                                  ...getAttendanceChipStyles(student.attendanceStatus || '—'),
                                  fontWeight: 700,
                                  fontFamily: 'Poppins, sans-serif',
                                }}
                              />
                            </TableCell>

                            <TableCell>
                              <Chip
                                size="small"
                                label={student.status || '—'}
                                sx={{
                                  ...getArrivalChipStyles(student.status || '—'),
                                  fontWeight: 700,
                                  fontFamily: 'Poppins, sans-serif',
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2.2,
            backgroundColor: '#ffffff',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Button
            onClick={handleExportSelectedSession}
            variant="contained"
            color="inherit"
            startIcon={<DownloadRoundedIcon sx={{ color: '#ffc400' }} />}
            sx={mainButtonStyles}
          >
            Export
          </Button>

          <Button
            onClick={handleCloseDetails}
            variant="contained"
            color="inherit"
            startIcon={<ExitToAppRoundedIcon sx={{ color: '#ffc400' }} />}
            sx={mainButtonStyles}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Reports;